import type { DocumentTableRow } from 'features/knowledgeDocumentViewer/components/advancedSearch/components/documentsTableView/DocumentTableColumns'
import type { DocumentPageMetadataUserEditValues } from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import {
  getCantileverDocumentEntryId,
  getSystemDocumentBqId,
} from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import {
  mergePageMetadataValues,
  preferUserEditValue,
} from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import {
  compareRevisionCodes,
  hasNewerRevision,
  type RevisionMenuItemView,
  type RevisionStamp,
} from 'features/knowledgeDocumentViewer/utils/revisionHelpers'

/** Shared revision-family list/nav fields. */
export type RevisionFamilyMemberView = {
  bqDocumentId: string
  pageNum: number
  numPages: number | null
  revisionCode: string | null
  revisionDate: string | null
  filename: string | null
  systemEntryId: string | undefined
  cantileverDocumentEntryId: string | undefined
  /** Stable list key (page entry id or document entry id). */
  memberEntryId: string
  isCurrent: boolean
}

export type PageRevisionFamilyMember = RevisionFamilyMemberView & {
  systemPage: SystemDocumentPageEntry
}

export function normalizeRevisionFamilyKeyPart(
  value: string | null | undefined
): string {
  return value?.trim().toUpperCase() ?? ''
}

export function pageRevisionFamilyKey(
  drawingNumber: string | null | undefined,
  sheetNumber: string | null | undefined
): string | undefined {
  const drawing = normalizeRevisionFamilyKeyPart(drawingNumber)
  if (!drawing) return undefined
  const sheet = normalizeRevisionFamilyKeyPart(sheetNumber)
  return `${drawing}:${sheet}`
}

/** Family key from system page + optional user-edit overlay. */
export function effectivePageRevisionFamilyKey(
  systemPage: SystemDocumentPageEntry | undefined,
  editValues: DocumentPageMetadataUserEditValues | undefined
): string | undefined {
  if (!systemPage) return undefined
  const merged = mergePageMetadataValues(systemPage.entry.data, editValues)
  return pageRevisionFamilyKey(
    merged.issuer_drawing_number,
    merged.sheet_number
  )
}

export function filterPagesInRevisionFamily(
  pages: readonly SystemDocumentPageEntry[],
  drawingNumber: string | null | undefined,
  sheetNumber: string | null | undefined
): SystemDocumentPageEntry[] {
  const familyKey = pageRevisionFamilyKey(drawingNumber, sheetNumber)
  if (!familyKey) return []
  return pages.filter((page) => {
    const key = pageRevisionFamilyKey(
      page.entry.data.issuer_drawing_number,
      page.entry.data.sheet_number
    )
    return key === familyKey
  })
}

/** Keep members whose effective family key still matches (handles leave-family). */
export function filterMembersByEffectivePageRevisionFamilyKey({
  members,
  currentFamilyKey,
  editValuesByPageEntryId,
}: {
  members: readonly PageRevisionFamilyMember[]
  currentFamilyKey: string | undefined
  editValuesByPageEntryId: ReadonlyMap<
    string,
    DocumentPageMetadataUserEditValues | undefined
  >
}): PageRevisionFamilyMember[] {
  if (!currentFamilyKey) return []
  return members.filter((member) => {
    const key = effectivePageRevisionFamilyKey(
      member.systemPage,
      editValuesByPageEntryId.get(member.memberEntryId)
    )
    return key === currentFamilyKey
  })
}

function compareNullableStringsDesc(
  first: string | null | undefined,
  second: string | null | undefined
): number {
  return (second ?? '').localeCompare(first ?? '')
}

/** Latest-first: revision date desc, then revision code desc. */
export function sortPagesByRevisionLatestFirst(
  pages: readonly SystemDocumentPageEntry[]
): SystemDocumentPageEntry[] {
  return [...pages].sort((a, b) => {
    const dateCompare = compareNullableStringsDesc(
      a.entry.data.latest_revision_date,
      b.entry.data.latest_revision_date
    )
    if (dateCompare !== 0) return dateCompare

    const codeA = a.entry.data.latest_revision?.trim() ?? ''
    const codeB = b.entry.data.latest_revision?.trim() ?? ''
    if (codeA && codeB) {
      return -compareRevisionCodes(codeA, codeB)
    }
    return compareNullableStringsDesc(codeA, codeB)
  })
}

export function buildPageRevisionFamilyMembers({
  pages,
  currentBqDocumentId,
  currentPageNum,
  documentsByBqId,
}: {
  pages: readonly SystemDocumentPageEntry[]
  currentBqDocumentId: string | undefined
  currentPageNum: number
  documentsByBqId: ReadonlyMap<string, SystemDocumentEntry>
}): PageRevisionFamilyMember[] {
  const sorted = sortPagesByRevisionLatestFirst(pages)
  const members: PageRevisionFamilyMember[] = []

  for (const page of sorted) {
    const bqDocumentId = page.entry.data.document_id?.trim()
    const pageNum = page.entry.data.page_num
    if (!bqDocumentId || pageNum === undefined || pageNum < 1) continue

    const parentDoc = documentsByBqId.get(bqDocumentId)
    const totalPages = parentDoc?.entry.data.total_pages
    members.push({
      bqDocumentId,
      pageNum,
      numPages:
        typeof totalPages === 'number' && totalPages > 0 ? totalPages : null,
      revisionCode: page.entry.data.latest_revision ?? null,
      revisionDate: page.entry.data.latest_revision_date ?? null,
      filename: parentDoc?.entry.data.filename ?? null,
      systemEntryId: parentDoc?.entry.id,
      cantileverDocumentEntryId: parentDoc
        ? getCantileverDocumentEntryId(parentDoc)
        : undefined,
      memberEntryId: page.entry.id,
      systemPage: page,
      isCurrent:
        currentBqDocumentId !== undefined &&
        bqDocumentId === currentBqDocumentId &&
        pageNum === currentPageNum,
    })
  }

  return members
}

/** Overlay page user-edits onto family member revision labels for display. */
export function applyPageUserEditsToRevisionFamilyMembers({
  members,
  editsByPageEntryId,
}: {
  members: readonly PageRevisionFamilyMember[]
  editsByPageEntryId: ReadonlyMap<
    string,
    DocumentPageMetadataUserEditValues | undefined
  >
}): PageRevisionFamilyMember[] {
  if (editsByPageEntryId.size === 0) {
    return [...members]
  }
  const next = members.map((member) => {
    const editValues = editsByPageEntryId.get(member.memberEntryId)
    if (editValues === undefined) {
      return member
    }
    const revisionCode =
      preferUserEditValue(editValues.latest_revision, member.revisionCode) ??
      null
    const revisionDate =
      preferUserEditValue(
        editValues.latest_revision_date,
        member.revisionDate
      ) ?? null
    if (
      revisionCode === member.revisionCode &&
      revisionDate === member.revisionDate
    ) {
      return member
    }
    return {
      ...member,
      revisionCode,
      revisionDate,
    }
  })
  return sortRevisionFamilyMembersLatestFirst(next)
}

function sortRevisionFamilyMembersLatestFirst(
  members: readonly PageRevisionFamilyMember[]
): PageRevisionFamilyMember[] {
  return [...members].sort((a, b) => {
    const dateCompare = compareNullableStringsDesc(
      a.revisionDate,
      b.revisionDate
    )
    if (dateCompare !== 0) return dateCompare
    const codeA = a.revisionCode?.trim() ?? ''
    const codeB = b.revisionCode?.trim() ?? ''
    if (codeA && codeB) {
      return -compareRevisionCodes(codeA, codeB)
    }
    return compareNullableStringsDesc(codeA, codeB)
  })
}

export function uniqueBqDocumentIdsFromPages(
  pages: readonly SystemDocumentPageEntry[]
): string[] {
  const ids: string[] = []
  const seen = new Set<string>()
  for (const page of pages) {
    const id = page.entry.data.document_id?.trim()
    if (!id || seen.has(id)) continue
    seen.add(id)
    ids.push(id)
  }
  return ids
}

export function systemDocumentsByBqIdMap(
  documents: readonly SystemDocumentEntry[]
): Map<string, SystemDocumentEntry> {
  const map = new Map<string, SystemDocumentEntry>()
  for (const document of documents) {
    const bqId = getSystemDocumentBqId(document)
    if (!bqId) continue
    map.set(bqId, document)
  }
  return map
}

export function pageRevisionMemberToTableRow(
  member: RevisionFamilyMemberView
): DocumentTableRow | null {
  if (!member.systemEntryId) return null
  const title = member.filename?.trim() || member.bqDocumentId
  return {
    id: member.bqDocumentId,
    systemEntryId: member.systemEntryId,
    title,
    filename: member.filename ?? '',
    documentTitle: member.filename,
    system: null,
    vendor: null,
    docType: null,
    fileType: null,
    drawing: null,
    timestamp: null,
    numPages: member.numPages,
    revisionCode: member.revisionCode,
    revisionDate: member.revisionDate,
    summary: null,
    keywords: [],
    bqDocumentId: member.bqDocumentId,
    cantileverDocumentEntryId: member.cantileverDocumentEntryId,
  }
}

export function isRevisionFamilyMemberOpenable(
  member: RevisionFamilyMemberView
): boolean {
  return pageRevisionMemberToTableRow(member) !== null
}

function revisionFamilyMemberStamp(
  member: RevisionFamilyMemberView
): RevisionStamp {
  return {
    key: member.memberEntryId,
    code: member.revisionCode,
    date: member.revisionDate,
  }
}

export function revisionFamilyMemberMenuItem(
  member: RevisionFamilyMemberView,
  isSelectable: boolean
): RevisionMenuItemView {
  return {
    ...revisionFamilyMemberStamp(member),
    isCurrent: member.isCurrent,
    isSelectable,
  }
}

export function revisionFamilyHasNewerRevision(
  members: readonly RevisionFamilyMemberView[]
): boolean {
  return hasNewerRevision(
    members.map(revisionFamilyMemberStamp),
    members.find((member) => member.isCurrent)?.memberEntryId
  )
}
